import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogIn, AlertCircle, Key, Hash, ArrowLeft, Mail, KeyRound, Maximize2, Smartphone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WidgetLogin() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const isLight = theme === 'light';
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loginMethod, setLoginMethod] = useState('password');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    booking_code: ''
  });
  const [isMobile, setIsMobile] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track analytics on mount
  useEffect(() => {
    const trackView = async () => {
      try {
        let sessionId = sessionStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('analytics_session', sessionId);
        }
        await axios.post(`${API}/analytics/track/${userId}`, {
          event_type: 'widget_view',
          page: 'widget_login',
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          session_id: sessionId
        });
      } catch (e) { /* Silent fail */ }
    };
    if (userId) trackView();
  }, [userId]);

  // Send resize message to parent for iframe auto-height
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'djcrm-resize', widget: 'login', height }, '*');
    };
    
    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    window.addEventListener('resize', sendHeight);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, [showForgotPassword, forgotPasswordSent, isMobile]);

  // Set body background color for proper iframe display
  useEffect(() => {
    document.body.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    document.documentElement.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isLight]);

  // Open login in full screen (new tab)
  const openFullScreen = () => {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) return;
    if (loginMethod === 'password' && !formData.password) return;
    if (loginMethod === 'code' && !formData.booking_code) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      let response;
      if (loginMethod === 'password') {
        response = await axios.post(`${API}/public/portal-login-password/${userId}`, {
          email: formData.email,
          password: formData.password
        });
      } else {
        response = await axios.post(`${API}/public/portal-login/${userId}`, {
          email: formData.email,
          booking_code: formData.booking_code
        });
      }
      
      if (response.data.portal_token) {
        window.location.href = `${BACKEND_URL}/portal/${response.data.portal_token}`;
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setError(error.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/client-forgot-password/${userId}`, { 
        email: forgotPasswordEmail 
      });
      setForgotPasswordSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setForgotPasswordSent(true);
      toast.success('If an account exists, a password reset email has been sent.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <div className={`min-h-fit py-6 flex items-center justify-center p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
        <div className="w-full max-w-md">
          <div className={`rounded-2xl p-8 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
            {forgotPasswordSent ? (
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-green-100' : 'bg-green-500/20'}`}>
                  <Mail className={`w-8 h-8 ${isLight ? 'text-green-600' : 'text-green-500'}`} />
                </div>
                <h2 className={`font-heading text-2xl font-bold mb-2 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Check your email</h2>
                <p className={`mb-6 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                  If an account exists for <strong className={isLight ? 'text-gray-900' : 'text-foreground'}>{forgotPasswordEmail}</strong>, we've sent a password reset link.
                </p>
                <p className={`text-sm mb-6 ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  The link will expire in 1 hour. Check your spam folder if you don't see it.
                </p>
                <Button
                  variant="outline"
                  className={`w-full ${isLight ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-glass-border'}`}
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSent(false);
                    setForgotPasswordEmail('');
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-blue-100' : 'bg-blue-500/20'}`}>
                    <KeyRound className={`w-8 h-8 ${isLight ? 'text-blue-600' : 'text-blue-500'}`} />
                  </div>
                  <h2 className={`font-heading text-2xl font-bold mb-2 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Forgot Password?</h2>
                  <p className={isLight ? 'text-gray-600' : 'text-muted-foreground'}>
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label className={isLight ? 'text-gray-700' : ''}>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="The email used for your booking"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className={isLight ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400' : 'bg-muted/20 border-glass-border'}
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full font-semibold py-6 ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-500/80 text-white'}`}
                    disabled={submitting}
                    data-testid="forgot-submit-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className={isLight ? 'w-full text-gray-600 hover:text-gray-900' : 'w-full text-muted-foreground'}
                    onClick={() => setShowForgotPassword(false)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-fit py-4 flex items-center justify-center p-3 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
      <div className="w-full max-w-md">
        <div className={`rounded-2xl p-6 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
          <div className="text-center mb-4">
            <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${isLight ? 'bg-green-100' : 'bg-green-500/20'}`}>
              <LogIn className={`w-6 h-6 ${isLight ? 'text-green-600' : 'text-green-500'}`} />
            </div>
            <h1 className={`font-heading text-xl font-bold mt-3 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Client Portal</h1>
            <p className={`mt-1 text-sm ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>Access your event details</p>
          </div>

          <Tabs value={loginMethod} onValueChange={setLoginMethod} className="space-y-3">
            <TabsList className={`w-full ${isLight ? 'bg-gray-100' : 'bg-muted/20'}`}>
              <TabsTrigger value="password" className={`flex-1 text-xs ${isLight ? 'data-[state=active]:bg-white data-[state=active]:text-gray-900' : ''}`}>
                <Key className="w-3 h-3 mr-1" />
                Password
              </TabsTrigger>
              <TabsTrigger value="code" className={`flex-1 text-xs ${isLight ? 'data-[state=active]:bg-white data-[state=active]:text-gray-900' : ''}`}>
                <Hash className="w-3 h-3 mr-1" />
                Booking Code
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label className={`text-sm ${isLight ? 'text-gray-700' : ''}`}>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="The email used for your booking"
                  required
                  className={isLight ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400' : 'bg-muted/20 border-glass-border'}
                  data-testid="login-email-input"
                />
              </div>

              <TabsContent value="password" className="mt-0 space-y-1">
                <div className="flex justify-between items-center">
                  <Label className={`text-sm ${isLight ? 'text-gray-700' : ''}`}>Password</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordEmail(formData.email);
                      setShowForgotPassword(true);
                    }}
                    className={`text-xs hover:underline ${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'}`}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Your portal password"
                  className={isLight ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400' : 'bg-muted/20 border-glass-border'}
                  data-testid="login-password-input"
                />
              </TabsContent>

              <TabsContent value="code" className="mt-0 space-y-1">
                <Label className={`text-sm ${isLight ? 'text-gray-700' : ''}`}>Booking Code</Label>
                <Input
                  value={formData.booking_code}
                  onChange={(e) => setFormData({ ...formData, booking_code: e.target.value })}
                  placeholder="Your unique booking code"
                  className={isLight ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400' : 'bg-muted/20 border-glass-border'}
                  data-testid="login-code-input"
                />
                <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  Check your booking confirmation email for this code
                </p>
              </TabsContent>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !formData.email || (loginMethod === 'password' ? !formData.password : !formData.booking_code)}
                className={`w-full font-semibold py-4 ${isLight ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-500/80 text-black'}`}
                data-testid="login-submit-btn"
              >
                {submitting ? 'Logging in...' : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Access Portal
                  </>
                )}
              </Button>
            </form>
          </Tabs>

          {/* Features section - collapsible on small heights */}
          <div className={`mt-4 pt-4 border-t ${isLight ? 'border-gray-200' : 'border-glass-border'}`}>
            <div className={`grid grid-cols-4 gap-1 text-xs ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>
              <div className="flex items-center gap-1">
                <span className={`w-1 h-1 rounded-full ${isLight ? 'bg-blue-500' : 'bg-neon-blue'}`}></span>
                Contracts
              </div>
              <div className="flex items-center gap-1">
                <span className={`w-1 h-1 rounded-full ${isLight ? 'bg-purple-500' : 'bg-neon-purple'}`}></span>
                Invoices
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-green-500"></span>
                Playlists
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-yellow-500"></span>
                Timeline
              </div>
            </div>
          </div>

          {/* Full Screen Option */}
          <div className={`mt-3 pt-3 border-t ${isLight ? 'border-gray-200' : 'border-glass-border'}`}>
            <button
              type="button"
              onClick={openFullScreen}
              className={`w-full flex items-center justify-center gap-2 text-xs transition-colors py-1 ${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'}`}
              data-testid="open-fullscreen-btn"
            >
              <Maximize2 className="w-3 h-3" />
              Open in full screen
            </button>
          </div>
        </div>
        
        <p className={`text-center text-xs mt-2 ${isLight ? 'text-gray-400' : 'text-muted-foreground'}`}>
          Powered by DJ CRM
        </p>
      </div>
    </div>
  );
}
